/**
 * The first-run tour (driver.js): the page dimmed, one element lit at a time
 * with a short blurb, Next to move on. It walks Home, then the sample
 * transition (components/transit/sample), then back Home to start your own.
 *
 * It starts on its own, on Home only (a deep link, an invited teammate's to a
 * section, is left alone), for a new account (under 3 days old) that hasn't
 * created a transition, until it's finished or closed in this browser.
 * "Take the tour" in the account menu (TRANSIT_TOUR) replays it any time.
 * Each step's element is marked data-tour="<target>" where it renders.
 */
import { useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { driver, type Driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { transitApi } from '../../services/transit'
import { useTransitSession } from './session'
import { SAMPLE_TRANSITION_ID } from './sample/data'
import { MAP, PACKAGE, READINESS } from './displayOrder'

/** Fire on window to start the tour. */
export const TRANSIT_TOUR = 'transit:tour'

const SEEN = 'transit.tourSeen'
const NEW_ACCOUNT_MS = 3 * 24 * 60 * 60 * 1000

const sample = (node: string) => `/transit/${SAMPLE_TRANSITION_ID}?node=${node}`

// path: where the step's element is (unset: the page of the last step before
// it that has one, so Back lands on the right page too). side: where the
// popover sits, when driver.js's own pick covers something the step names.
const STEPS: Array<{ path?: string; target: string; title: string; text: string; side?: 'top' | 'bottom' | 'left' | 'right' }> = [
  { path: '/transit', target: 'new-transition', title: 'New Transition',
    text: 'Start every transition here. It opens this page, where you bring the documents for the technology.' },
  { target: 'home-required', title: 'What we need',
    text: 'Bring whatever documentation you have for the technology. A completed vendor questionnaire is required, and you can download a blank one here.' },
  { target: 'home-upload', title: 'Start a transition',
    text: 'Drop the documents here. Overmatch reads each one, sorts it by type, and drafts the transition package from them.' },
  { target: 'sample', title: 'A finished example',
    text: 'TERN-4 is an example transition for a fictional small drone. Next, we open it to see what you get.' },
  { path: sample('company_readiness'), target: 'panel', title: 'Questions',
    text: "When the documents don't say enough, a section asks. Answer here, and its next draft uses your answers." },
  { target: 'sections', title: 'Sections',
    text: 'The package is split into sections. Switch between them here or in the sidebar; the dots show where each one stands.' },
  { target: 'status', title: 'Status',
    text: "Each section drafts itself from your documents, then waits for you: Needs input when it has questions, Review when it's ready to read, Done once you approve it." },
  { target: 'draft', title: 'The draft',
    text: 'Written from your documents, with every source listed underneath. Edit the text in place, or redraft it.' },
  { path: sample('vendor_costs'), target: 'approve', title: 'Approve',
    text: 'Read the draft through, then approve it. Approving lets the sections that depend on it start drafting.' },
  { target: 'next-section', title: 'Go to next section', side: 'top',
    text: 'Once a section is approved, this takes you to the next one in order. After the last, it opens Generate package.' },
  { target: 'context', title: 'Transition Context',
    text: "Your documents, notes you've written in, and the policies the drafts follow. Add more at any time." },
  { path: sample(READINESS), target: 'readiness', title: 'Readiness',
    text: "TRL, MRL, IRL and the company's CRL, computed from the facts in your documents, each with its source." },
  { path: sample(MAP), target: 'map', title: 'How it fits together',
    text: 'A section drafts as soon as the sections it depends on are approved.' },
  { path: sample(PACKAGE), target: 'package', title: 'Generate package',
    text: 'The whole document as it stands, every section in order. Once all ten sections are approved, generate the Word package here.' },
  { path: '/transit', target: 'home-upload', title: 'Your turn',
    text: 'Drop your documents here to start your own. You can take this tour again from your account menu.' },
]

const element = (target: string) => document.querySelector(`[data-tour="${target}"]`)

/** Resolves once the target has rendered (a route change, a load), or after 5 seconds. */
function rendered(target: string): Promise<void> {
  return new Promise((resolve) => {
    const until = Date.now() + 5000
    const check = () => (element(target) || Date.now() > until ? resolve() : requestAnimationFrame(check))
    check()
  })
}

const seen = () => {
  try {
    return localStorage.getItem(SEEN) === '1'
  } catch {
    return false
  }
}

export default function TransitTour() {
  const { user } = useTransitSession()
  // useNavigate changes on every route change; the tour must not restart with it.
  const nav = useNavigate()
  const navigate = useRef(nav)
  navigate.current = nav
  const tour = useRef<Driver | null>(null)

  const start = useCallback(() => {
    tour.current?.destroy()
    // Set once it's closed: a step still waiting for its element must not
    // bring the overlay back (driver.js would draw it with nothing to close it).
    let closed = false
    // Go to step i: its page first, then wait for its element.
    const go = async (i: number) => {
      const path = STEPS.slice(0, i + 1).reverse().find((s) => s.path)?.path
      if (path && window.location.pathname + window.location.search !== path) navigate.current(path)
      await rendered(STEPS[i].target)
      if (closed) return
      if (i === 0 && !d.isActive()) d.drive(0)
      else d.moveTo(i)
    }
    const d = driver({
      steps: STEPS.map((s) => ({
        element: () => element(s.target) ?? document.body,
        popover: { title: s.title, description: s.text, side: s.side },
      })),
      showProgress: true,
      progressText: '{{current}} of {{total}}',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Done',
      overlayOpacity: 0.55,
      stagePadding: 6,
      stageRadius: 8,
      popoverClass: 'transit-tour',
      // Only Next, Back, the close button and Escape move or end it.
      overlayClickBehavior: 'none',
      onNextClick: () => {
        const i = d.getActiveIndex() ?? 0
        if (d.isLastStep()) d.destroy()
        else go(i + 1)
      },
      onPrevClick: () => go(Math.max(0, (d.getActiveIndex() ?? 0) - 1)),
      onDestroyed: () => {
        closed = true
        try {
          localStorage.setItem(SEEN, '1')
        } catch {
          // Storage blocked: it may show again next visit.
        }
      },
    })
    tour.current = d
    go(0)
  }, [])

  // A new account with no transition of its own, the first time in this
  // browser, once it's on Home; offered once per visit, so the tour's own
  // returns to Home don't restart it.
  const { pathname } = useLocation()
  const offered = useRef(false)
  useEffect(() => {
    if (offered.current || pathname !== '/transit') return
    if (seen() || Date.now() - Date.parse(user.created_at) > NEW_ACCOUNT_MS) return
    let live = true
    transitApi.listTransitions()
      .then((ts) => {
        if (!live || offered.current || ts.some((t) => t.created_by === user.id)) return
        offered.current = true
        start()
      })
      .catch(() => undefined)
    return () => { live = false }
  }, [pathname, user, start])

  useEffect(() => {
    window.addEventListener(TRANSIT_TOUR, start)
    return () => {
      window.removeEventListener(TRANSIT_TOUR, start)
      tour.current?.destroy()
    }
  }, [start])

  return null
}
